"use client";

import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import { useMemo, useState } from "react";
import { getTitleComponents } from "@/helpers";
import { BlogArticleCard } from "../Blog/Article";

const ALL_CATEGORY = "Toutes les catégories";

const categoryLabel = (category) => {
  if (category === ALL_CATEGORY) {
    return "Toutes les catégories";
  }
  return category;
};

/**
 * @typedef {import("@prismicio/client").Content.BlogListSlice} BlogListSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<BlogListSlice>} BlogListProps
 * @type {import("react").FC<BlogListProps>}
 */
const BlogList = ({ slice }) => {
  const { title, filter_title, articles } = slice.primary;
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORY);

  const filledArticles = useMemo(
    () =>
      Array.isArray(articles)
        ? articles.filter((item) => isFilled.contentRelationship(item.article))
        : [],
    [articles],
  );

  const categories = useMemo(() => {
    const types = filledArticles
      .map((item) => item.article.data?.type)
      .filter((type) => isFilled.keyText(type));

    return [ALL_CATEGORY, ...Array.from(new Set(types))];
  }, [filledArticles]);

  const filteredArticles = useMemo(() => {
    if (activeCategory === ALL_CATEGORY) {
      return filledArticles;
    }

    return filledArticles.filter(
      (item) => item.article.data?.type === activeCategory,
    );
  }, [activeCategory, filledArticles]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 padding-x padding-y-first"
    >
      <div className="framing-lg flex flex-col gap-10 md:gap-20 md:pt-[80px] lg:pt-[30px]">
        {isFilled.richText(title) && (
          <PrismicRichText
            field={title}
            components={{
              heading1: ({ children }) => (
                <h1 className="h1-n lg:dk text-center text-bleu">
                  {children}
                </h1>
              ),
            }}
          />
        )}

        <div className="flex flex-col gap-10 items-start justify-start">
          {categories.length > 1 && (
            <div className="flex w-full min-w-0 flex-col gap-4 md:items-start">
              {isFilled.keyText(filter_title) && (
                <p className="text-xs font-bold uppercase leading-[14px] text-bleu-50 font-body">
                  {filter_title}
                </p>
              )}

              <div className="-mx-5 flex snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-smooth px-5 scroll-px-5 [-ms-overflow-style:none] [scrollbar-width:none] [overscroll-behavior-x:contain] touch-pan-x [&::-webkit-scrollbar]:hidden md:mx-0 md:flex-wrap md:overflow-visible md:px-0 md:scroll-px-0 md:snap-none">
                {categories.map((category) => {
                  const isActive = activeCategory === category;

                  return (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setActiveCategory(category)}
                      className={`p-sm-n nm shrink-0 snap-start whitespace-nowrap rounded-[6px] px-[10px] py-[6px] cursor-pointer ${isActive
                        ? "bg-vert text-blanc"
                        : "bg-vert-10 text-bleu"
                        }`}>
                      {categoryLabel(category)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {filteredArticles.length > 0 && (
            <div className="flex flex-col gap-10 md:grid md:grid-cols-2 lg:grid-cols-3 lg:gap-10 lg:gap-y-20 w-full">
              {filteredArticles.map(({ article }, index) => (
                <BlogArticleCard key={`article-${index}`} article={article} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default BlogList;
